import { useCallback, useContext, useMemo } from 'react'

import { useStaticData } from '@mc/static-data-react'

import { PlansContext } from '../context/plansContext'
import { useTiersInfo } from './useTiersInfo'
import { trackCheckoutClick } from '../events'

export const usePlansContext = () => {
  const { selectedPlanId, setSelectedPlanId } = useContext(PlansContext)
  const { individual, duo, family, familyPlus } = useTiersInfo()
  const { isGift } = useStaticData<{ isGift: boolean }>()

  const isIndividualSelected = useMemo(
    () => selectedPlanId === individual?.productId,
    [selectedPlanId, individual],
  )
  const isDuoSelected = useMemo(
    () => selectedPlanId === duo?.productId,
    [selectedPlanId, duo],
  )
  const isFamilySelected = useMemo(
    () => selectedPlanId === family?.productId,
    [selectedPlanId, family],
  )
  const isFamilyPlusSelected = useMemo(
    () => selectedPlanId === familyPlus?.productId,
    [selectedPlanId, familyPlus],
  )

  const selectedPlan = useMemo(() => {
    if (isIndividualSelected) {
      return individual
    } else if (isDuoSelected) {
      return duo
    } else if (isFamilySelected) {
      return family
    } else if (isFamilyPlusSelected) {
      return familyPlus
    }

    return undefined
  }, [
    isIndividualSelected,
    isDuoSelected,
    isFamilySelected,
    isFamilyPlusSelected,
    individual,
    duo,
    family,
    familyPlus,
  ])

  const selectIndividual = useCallback(
    () => setSelectedPlanId(individual?.productId),
    [setSelectedPlanId, individual],
  )

  const selectDuo = useCallback(
    () => setSelectedPlanId(duo?.productId),
    [setSelectedPlanId, duo],
  )

  const selectFamily = useCallback(
    () => setSelectedPlanId(family?.productId),
    [setSelectedPlanId, family],
  )

  const selectFamilyPlus = useCallback(
    () => setSelectedPlanId(familyPlus?.productId),
    [setSelectedPlanId, familyPlus],
  )

  const onClickIndividual = useCallback(() => {
    selectIndividual()
    trackCheckoutClick(individual)
  }, [selectIndividual, individual])

  const onClickDuo = useCallback(() => {
    selectDuo()
    trackCheckoutClick(duo)
  }, [selectDuo, duo])

  const onClickFamily = useCallback(() => {
    selectFamily()
    trackCheckoutClick(family)
  }, [selectFamily, family])

  const onClickFamilyPlus = useCallback(() => {
    selectFamilyPlus()
    trackCheckoutClick(familyPlus)
  }, [selectFamilyPlus, familyPlus])

  return {
    isGift,
    isIndividualSelected,
    isDuoSelected,
    isFamilySelected,
    isFamilyPlusSelected,
    selectIndividual,
    selectDuo,
    selectFamily,
    selectFamilyPlus,
    onClickIndividual,
    onClickDuo,
    onClickFamily,
    onClickFamilyPlus,
    selectedPlan,
  }
}
